import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { authApi, accountApi, notificationApi } from '../utils/api';
import { 
  Wallet, 
  Coins, 
  ArrowRightLeft, 
  Bell, 
  FileText,
  TrendingUp,
  Clock,
  Sparkles
} from 'lucide-react';

export default function Dashboard() {
  const user = useAuthStore((state) => state.user);
  const updateUser = useAuthStore((state) => state.updateUser);

  const { data: userData } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const response = await authApi.getMe();
      updateUser(response.data);
      return response.data;
    },
  });

  const { data: notificationsData } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: async () => {
      const response = await notificationApi.getAll({ unreadOnly: true, limit: 5 });
      return response.data;
    },
  });

  const { data: ledgerData } = useQuery({
    queryKey: ['ledger', 'recent'],
    queryFn: async () => {
      const response = await accountApi.getLedger({ limit: 5 });
      return response.data;
    },
  });

  const quickActions = [
    { icon: ArrowRightLeft, label: '轉帳', gradient: 'from-blue-500 to-cyan-500', path: '/transfer' },
    { icon: Coins, label: '兌換', gradient: 'from-purple-500 to-pink-500', path: '/exchange' },
    { icon: FileText, label: '存摺', gradient: 'from-emerald-500 to-teal-500', path: '/passbook' },
    { icon: Bell, label: '通知', gradient: 'from-orange-500 to-red-500', path: '/notifications' },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            歡迎回來，<span className="gradient-text">{user?.name}</span>
          </h1>
          <p className="text-slate-500 mt-1">這是您的儀表板概覽</p>
        </div>
        <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
          user?.role === 'admin' ? 'bg-red-100 text-red-700' : 
          user?.role === 'teacher' ? 'bg-amber-100 text-amber-700' : 
          'bg-blue-100 text-blue-700'
        }`}>
          <Sparkles className="w-4 h-4 mr-2" />
          {user?.role === 'admin' ? '管理員' : user?.role === 'teacher' ? '老師' : '學生'}
        </span>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 text-white shadow-xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="relative p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">新臺幣餘額</p>
                <h2 className="text-4xl font-bold mt-2">
                  ${userData?.ntBalance?.toLocaleString() || user?.ntBalance?.toLocaleString() || 0}
                </h2>
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Wallet className="h-7 w-7 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 to-purple-800 text-white shadow-xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="relative p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">數資幣餘額</p>
                <h2 className="text-3xl font-bold mt-2">
                  {userData?.msBalance?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || 
                   user?.msBalance?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || 0} <span className="text-lg">MS∮</span>
                </h2>
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Coins className="h-7 w-7 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickActions.map((action) => (
          <a
            key={action.label}
            href={action.path}
            className={`group relative overflow-hidden rounded-xl bg-gradient-to-br ${action.gradient} p-5 shadow-lg transition-all hover:shadow-xl hover:scale-[1.02]`}
          >
            <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors" />
            <div className="relative flex flex-col items-center gap-3 text-white">
              <action.icon className="h-8 w-8" />
              <span className="font-semibold">{action.label}</span>
            </div>
          </a>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">最近交易</h3>
            </div>
          </div>
          <div className="p-6">
            {ledgerData?.records?.length === 0 ? (
              <p className="text-slate-400 text-center py-8">暫無交易記錄</p>
            ) : (
              <div className="space-y-4">
                {ledgerData?.records?.map((record: any) => (
                  <div key={record.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${record.amount > 0 ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                      <div>
                        <p className="font-medium text-slate-900">{record.reason}</p>
                        <p className="text-xs text-slate-400">{new Date(record.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <span className={`font-bold ${record.amount > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {record.amount > 0 ? '+' : ''}{record.amount}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                  <Bell className="h-5 w-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">未讀通知</h3>
              </div>
              {notificationsData?.unreadCount > 0 && (
                <span className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-medium">
                  {notificationsData?.unreadCount} 則
                </span>
              )}
            </div>
          </div>
          <div className="p-6">
            {notificationsData?.notifications?.length === 0 ? (
              <p className="text-slate-400 text-center py-8">暫無未讀通知</p>
            ) : (
              <div className="space-y-4">
                {notificationsData?.notifications?.map((notif: any) => (
                  <div key={notif.id} className="p-4 bg-slate-50 rounded-xl border-l-4 border-blue-500">
                    <p className="font-medium text-slate-900">{notif.title || '通知'}</p>
                    <p className="text-sm text-slate-600 line-clamp-2">{notif.content}</p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                      <Clock className="h-3 w-3" />
                      {new Date(notif.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
